import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { ProcurementItem, ProcurementRequest, ProcurementStatus, ProcurementPriority, User } from '../types';
import { procurementAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type ProcurementTabValue = 'all' | ProcurementStatus;

const STATUS_TABS: Array<{ label: string; value: ProcurementTabValue }> = [
  { label: 'Svi zahtevi', value: 'all' },
  { label: 'Na čekanju', value: 'pending' },
  { label: 'Odobren', value: 'approved' },
  { label: 'Isporučeno', value: 'delivered' },
  { label: 'Verifikovano', value: 'verified' },
  { label: 'Odbijen', value: 'rejected' },
  { label: 'Odbijen sa povratnim informacijama', value: 'rejected_with_feedback' }
];

const statusLabel = (s: ProcurementStatus) => STATUS_TABS.find((t) => t.value === s)?.label || s;

const statusSeverity = (s: ProcurementStatus): any => {
  if (s === 'pending') return 'warning';
  if (s === 'approved') return 'success';
  if (s === 'verified') return 'info';
  if (s === 'delivered') return 'help';
  if (s === 'rejected' || s === 'rejected_with_feedback') return 'danger';
  return 'secondary';
};

const priorityLabel = (p: ProcurementPriority | undefined) => {
  if (!p) return 'Srednja';
  if (p === 'low') return 'Niska';
  if (p === 'medium') return 'Srednja';
  if (p === 'high') return 'Visoka';
  return 'Hitno';
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('sr-RS');
  } catch {
    return iso;
  }
};

const unitOptions = [
  { label: 'komad', value: 'komad' },
  { label: 'kg', value: 'kg' },
  { label: 'g', value: 'g' },
  { label: 'l', value: 'l' },
  { label: 'm', value: 'm' },
  { label: 'set', value: 'set' }
];

const priorityOptions = [
  { label: 'Niska', value: 'low' },
  { label: 'Srednja', value: 'medium' },
  { label: 'Visoka', value: 'high' },
  { label: 'Hitno', value: 'urgent' }
];

// Draft of an in-progress (not yet submitted) new request, kept per-user so a
// session timeout, closed tab or reload doesn't force retyping the whole thing.
const DRAFT_STORAGE_PREFIX = 'procurement_draft_';

interface ProcurementDraft {
  items: ProcurementItem[];
  notes: string;
}

const getDraftKey = (userId?: string) => `${DRAFT_STORAGE_PREFIX}${userId || 'anon'}`;

const loadDraft = (userId?: string): ProcurementDraft | null => {
  try {
    const raw = localStorage.getItem(getDraftKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveDraft = (userId: string | undefined, draft: ProcurementDraft) => {
  try {
    localStorage.setItem(getDraftKey(userId), JSON.stringify(draft));
  } catch {
    // localStorage unavailable (private mode / quota) - draft is best-effort
  }
};

const clearDraft = (userId?: string) => {
  try {
    localStorage.removeItem(getDraftKey(userId));
  } catch {
    // ignore
  }
};

const ProcurementRequests: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [requests, setRequests] = useState<ProcurementRequest[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');

  // Create / edit dialog
  const [createVisible, setCreateVisible] = useState(false);
  const [editingRequest, setEditingRequest] = useState<ProcurementRequest | null>(null);
  const [items, setItems] = useState<ProcurementItem[]>([]);
  const [notes, setNotes] = useState('');

  const [itemDraft, setItemDraft] = useState<ProcurementItem>({
    productName: '',
    quantity: 1,
    unit: 'komad',
    priority: 'medium',
    isSparePart: false,
    link: '',
    description: ''
  });

  // View dialog
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ProcurementRequest | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Delivery confirmation modal (end user)
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState<ProcurementRequest | null>(null);
  const [confirmNote, setConfirmNote] = useState('');

  // Item description dialog
  const [descVisible, setDescVisible] = useState(false);
  const [descTitle, setDescTitle] = useState('');
  const [descText, setDescText] = useState('');

  const canCreate = user?.role !== 'visitor';
  // Logistika prima narudžbine i telefonom, pa jedina može da unese zahtev u ime drugog korisnika
  const canCreateOnBehalf = user?.role === 'logistika';
  const [requestUsers, setRequestUsers] = useState<User[]>([]);
  const [requestedForUserId, setRequestedForUserId] = useState('');
  const canProcess = user?.role === 'logistika' || user?.role === 'admin';

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const downloadRequestPdf = async (req: ProcurementRequest) => {
    try {
      const blob = await procurementAPI.downloadRequestPdf(req._id);
      downloadBlob(blob, `procurement-request-${req.requestId}.pdf`);
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Ne mogu da preuzmem PDF zahteva',
        life: 5000
      });
    }
  };

  const downloadDeliveryPdf = async (req: ProcurementRequest) => {
    try {
      const blob = await procurementAPI.downloadDeliveryPdf(req._id);
      downloadBlob(blob, `procurement-delivery-${req.requestId}.pdf`);
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Ne mogu da preuzmem PDF isporuke',
        life: 5000
      });
    }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await procurementAPI.getAll();
      setRequests(data);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || 'Greška pri učitavanju zahteva');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If user navigates to /procurement/create, auto-open create dialog (then normalize URL).
  useEffect(() => {
    if (location.pathname.endsWith('/procurement/create')) {
      if (canCreate) {
        openCreate();
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'Nije dozvoljeno',
          detail: 'Nemate dozvolu da kreirate zahteve za nabavku.',
          life: 3500
        });
      }
      navigate('/procurement', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const filteredByActiveTab = useMemo(() => {
    const status = STATUS_TABS[activeTab]?.value || 'all';
    const base = status === 'all' ? requests : requests.filter((r) => r.status === status);
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => {
      const id = (r.requestId || '').toLowerCase();
      const itemMatch = (r.items || []).some((item) => {
        const productName = (item.productName || '').toLowerCase();
        const description = (item.description || '').toLowerCase();
        const link = (item.link || '').toLowerCase();
        return productName.includes(q) || description.includes(q) || link.includes(q);
      });
      if (!canProcess) {
        return id.includes(q) || itemMatch;
      }
      const username = (r.requestedBy?.username || '').toLowerCase();
      const full = `${r.requestedBy?.firstName || ''} ${r.requestedBy?.lastName || ''}`.toLowerCase();
      return id.includes(q) || username.includes(q) || full.includes(q) || itemMatch;
    });
  }, [requests, activeTab, canProcess, search]);

  const loadRequestUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setRequestUsers(
        data
          .filter((u) => u._id !== user?._id && !['visitor', 'supplier', 'customer'].includes(u.role))
          .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'sr'))
      );
    } catch {
      toast.current?.show({ severity: 'warn', summary: 'Korisnici', detail: 'Lista korisnika nije učitana, zahtev se može podneti samo u vaše ime.', life: 4000 });
    }
  };

  const openCreate = () => {
    setEditingRequest(null);
    setRequestedForUserId('');
    if (canCreateOnBehalf) void loadRequestUsers();
    const saved = loadDraft(user?._id);
    if (saved && (saved.items.length > 0 || saved.notes.trim())) {
      setItems(saved.items);
      setNotes(saved.notes);
      toast.current?.show({
        severity: 'info',
        summary: 'Nacrt vraćen',
        detail: 'Vraćene su stavke koje ste prethodno uneli, a niste poslali.',
        life: 4000
      });
    } else {
      setItems([]);
      setNotes('');
    }
    setItemDraft({
      productName: '',
      quantity: 1,
      unit: 'komad',
      priority: 'medium',
      isSparePart: false,
      link: '',
      description: ''
    });
    setCreateVisible(true);
  };

  const discardDraft = () => {
    clearDraft(user?._id);
    setItems([]);
    setNotes('');
  };

  // Persist the in-progress "new request" form as the user types, so an
  // expired session, closed tab or crash doesn't lose their unsaved items.
  useEffect(() => {
    if (!createVisible || editingRequest) return;
    saveDraft(user?._id, { items, notes });
  }, [items, notes, createVisible, editingRequest, user?._id]);

  const openEditResubmit = (req: ProcurementRequest) => {
    setEditingRequest(req);
    setItems(req.items || []);
    setNotes(req.notes || '');
    setItemDraft({
      productName: '',
      quantity: 1,
      unit: 'komad',
      priority: 'medium',
      isSparePart: false,
      link: '',
      description: ''
    });
    setCreateVisible(true);
  };

  const addItem = () => {
    const name = itemDraft.productName.trim();
    if (!name) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Naziv proizvoda je obavezan', life: 2500 });
      return;
    }
    if (!itemDraft.quantity || itemDraft.quantity <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Količina mora biti veća od 0', life: 2500 });
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        productName: name,
        quantity: itemDraft.quantity,
        unit: itemDraft.unit,
        priority: itemDraft.priority,
        isSparePart: !!itemDraft.isSparePart,
        link: itemDraft.link?.trim() || '',
        description: itemDraft.description?.trim() || ''
      }
    ]);
    setItemDraft((p) => ({ ...p, productName: '', quantity: 1, link: '', description: '' }));
  };

  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const submitRequest = async () => {
    if (!items.length) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Dodajte bar jednu stavku', life: 2500 });
      return;
    }
    setLoading(true);
    try {
      if (editingRequest) {
        const res = await procurementAPI.updateAndResubmit(editingRequest._id, { items, notes });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: res.message, life: 3000 });
      } else {
        await procurementAPI.create({ items, notes, ...(requestedForUserId ? { requestedForUserId } : {}) });
        clearDraft(user?._id);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Zahtev je kreiran', life: 3000 });
      }
      setCreateVisible(false);
      await loadRequests();
      setActiveTab(0); // pending
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Greška pri čuvanju zahteva',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const openView = (req: ProcurementRequest) => {
    setSelectedRequest(req);
    setDecisionNote('');
    setRejectionReason(req.rejectionReason || req.feedback || '');
    setViewVisible(true);
  };

  const openConfirmDelivery = (req: ProcurementRequest) => {
    setConfirmRequest(req);
    setConfirmNote('');
    setConfirmVisible(true);
  };

  const openDescription = (productName: string, description: string) => {
    setDescTitle(productName);
    setDescText(description);
    setDescVisible(true);
  };

  const doDelete = async (req: ProcurementRequest) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati zahtev ${req.requestId}?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await procurementAPI.delete(req._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Zahtev je obrisan', life: 2500 });
          await loadRequests();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.message || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const updateStatus = async (req: ProcurementRequest, status: ProcurementStatus | 'delivered', reason?: string) => {
    setLoading(true);
    try {
      const res = await procurementAPI.updateStatus(req._id, { status, rejectionReason: reason });
      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: res.message, life: 3000 });
      if (selectedRequest && selectedRequest._id === req._id) setSelectedRequest(res.request);
      await loadRequests();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Greška pri promeni statusa',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeliveryFromModal = async (decision: 'confirm' | 'reject') => {
    if (!confirmRequest) return;
    setLoading(true);
    try {
      const res = await procurementAPI.confirmDelivery(confirmRequest._id, { decision, note: confirmNote });
      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: res.message, life: 3000 });
      setConfirmRequest(res.request);
      setConfirmVisible(false);
      await loadRequests();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.message || 'Greška pri potvrdi isporuke',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBody = (row: ProcurementRequest) => <Tag value={statusLabel(row.status)} severity={statusSeverity(row.status)} />;

  const actionsBody = (row: ProcurementRequest) => {
    const canDelete = user?.role === 'admin' || user?.role === 'logistika' || user?.role === 'odrzavanje';
    const isRequester = !!user && row.requestedBy?._id === user._id;
    const canConfirm = row.status === 'delivered' && (user?.role === 'admin' || isRequester);
    const canMarkDelivered = canProcess && row.status === 'approved';
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-eye" className="p-button-sm" onClick={() => openView(row)} />
        <Button
          icon="pi pi-file-pdf"
          className="p-button-sm"
          severity="danger"
          outlined
          onClick={() => downloadRequestPdf(row)}
          tooltip="Štampaj PDF"
          tooltipOptions={{ position: 'top' }}
        />
        {canConfirm && (
          <Button
            icon="pi pi-check-square"
            className="p-button-sm"
            severity="success"
            onClick={() => openConfirmDelivery(row)}
            tooltip="Potvrdi isporuku"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {canMarkDelivered && (
          <Button
            icon="pi pi-box"
            className="p-button-sm"
            severity="help"
            onClick={() => updateStatus(row, 'delivered')}
            tooltip="Isporučeno"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {canDelete && <Button icon="pi pi-trash" className="p-button-sm" severity="danger" onClick={() => doDelete(row)} />}
      </div>
    );
  };

  const createFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setCreateVisible(false)} />
      <Button
        label={editingRequest ? 'Ponovo pošalji' : 'Kreiraj zahtev'}
        icon="pi pi-check"
        severity="info"
        onClick={submitRequest}
        loading={loading}
      />
    </div>
  );

  const viewFooter = (
    <div className="flex justify-content-end gap-2 flex-wrap">
      {selectedRequest?.status === 'rejected_with_feedback' &&
        user &&
        (user.role === 'admin' ||
          user.role === 'magacin' ||
          user.role === 'direktor' ||
          user.role === 'lager' ||
          user.role === 'odrzavanje' ||
          selectedRequest.requestedBy._id === user._id) && (
          <Button
            label="Izmeni i ponovo pošalji"
            icon="pi pi-pencil"
            onClick={() => {
              setViewVisible(false);
              openEditResubmit(selectedRequest);
            }}
            severity="info"
          />
        )}

      {canProcess && selectedRequest?.status === 'pending' && (
        <>
          <Button label="Odobri" icon="pi pi-check" severity="success" onClick={() => updateStatus(selectedRequest, 'approved')} />
          <Button
            label="Odbij"
            icon="pi pi-times"
            severity="danger"
            onClick={() => updateStatus(selectedRequest, 'rejected', rejectionReason)}
            disabled={!rejectionReason.trim()}
          />
          <Button
            label="Odbij sa feedback"
            icon="pi pi-comment"
            severity="warning"
            onClick={() => updateStatus(selectedRequest, 'rejected_with_feedback', rejectionReason)}
            disabled={!rejectionReason.trim()}
          />
        </>
      )}

      {canProcess && selectedRequest?.status === 'approved' && (
        <Button
          label="Označi isporučeno"
          icon="pi pi-truck"
          severity="help"
          onClick={() => updateStatus(selectedRequest, 'delivered')}
        />
      )}

      {selectedRequest?.status === 'delivered' &&
        user &&
        (user.role === 'admin' || selectedRequest.requestedBy._id === user._id) && (
          <Button
            label="Potvrdi isporuku"
            icon="pi pi-check-square"
            severity="success"
            onClick={() => {
              setViewVisible(false);
              openConfirmDelivery(selectedRequest);
            }}
          />
        )}

      <Button label="Zatvori" className="p-button-secondary p-button-outlined" onClick={() => setViewVisible(false)} />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Zahtevi za nabavku</h2>
          <div className="text-600 mt-1">{canProcess ? 'Svi zahtevi za nabavku' : 'Moji zahtevi za nabavku'}</div>
        </div>
        {canCreate && (
          <Button label="Kreiraj novi zahtev" icon="pi pi-plus" severity="info" onClick={openCreate} />
        )}
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="mb-3">
          <span className="w-full" style={{ position: 'relative', display: 'block' }}>
            {search && (
              <i
                className="pi pi-times cursor-pointer"
                onClick={() => setSearch('')}
                title="Obriši pretragu"
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}
              />
            )}
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                canProcess
                  ? 'Pretraži po ID-u, podnosiocu ili stavkama zahteva (npr. vijak)...'
                  : 'Pretraži po ID-u ili stavkama zahteva (npr. vijak)...'
              }
              className="w-full"
              style={{ paddingRight: search ? '2.2rem' : undefined }}
            />
          </span>
        </div>
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          {STATUS_TABS.map((t) => (
            <TabPanel header={t.label} key={t.value}>
              <DataTable
                value={filteredByActiveTab}
                loading={loading}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                emptyMessage="Nema zahteva za nabavku u ovoj kategoriji"
                className="p-datatable-sm"
                sortField="createdAt"
                sortOrder={-1}
                responsiveLayout="stack"
                breakpoint="768px"
              >
                <Column field="requestId" header="ID zahteva" sortable />
                {canProcess && (
                  <Column
                    header="Podnosilac"
                    body={(r: ProcurementRequest) => (r.requestedBy ? r.requestedBy.username : '-')}
                    style={{ width: '190px' }}
                  />
                )}
                {canProcess && (
                  <Column
                    header="Uloga"
                    body={(r: ProcurementRequest) => (r.requestedBy ? r.requestedBy.role : '-')}
                    style={{ width: '140px' }}
                  />
                )}
                <Column header="Broj stavki" body={(r: ProcurementRequest) => r.items?.length || 0} style={{ width: '140px' }} />
                <Column header="Status" body={statusBody} style={{ width: '220px' }} />
                <Column header="Datum kreiranja" body={(r: ProcurementRequest) => formatDate(r.createdAt)} sortable style={{ width: '170px' }} />
                <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />
              </DataTable>
            </TabPanel>
          ))}
        </TabView>
      </Card>

      <Dialog
        header={editingRequest ? `Izmeni zahtev ${editingRequest.requestId}` : 'Kreiraj novi zahtev za nabavku'}
        visible={createVisible}
        onHide={() => setCreateVisible(false)}
        // Full screen dialog (requested)
        maximized
        style={{ width: '100vw', height: '100vh' }}
        contentStyle={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}
        footer={createFooter}
        draggable={false}
        modal
        className="procurement-dialog"
      >
        <div className="p-fluid">
          {canCreateOnBehalf && !editingRequest && (
            <div className="field mb-4">
              <label>Za koga se podnosi zahtev (opciono)</label>
              <Dropdown
                value={requestedForUserId}
                options={requestUsers}
                optionValue="_id"
                optionLabel="username"
                itemTemplate={(u: User) => `${u.firstName} ${u.lastName} (${u.username})`}
                valueTemplate={(u: User | null, props) => (u ? `${u.firstName} ${u.lastName} (${u.username})` : props.placeholder)}
                filter
                filterBy="firstName,lastName,username"
                showClear
                placeholder="Ja (podrazumevano)"
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
                onChange={(e) => setRequestedForUserId(e.value || '')}
              />
            </div>
          )}
          <div className="flex align-items-center justify-content-between mb-3">
            <h3 className="m-0">Dodaj stavke</h3>
            {!editingRequest && items.length > 0 && (
              <Button label="Isprazni formu" icon="pi pi-trash" text severity="secondary" onClick={discardDraft} />
            )}
          </div>

          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Naziv proizvoda *</label>
                <InputText value={itemDraft.productName} onChange={(e) => setItemDraft((p) => ({ ...p, productName: e.target.value }))} placeholder="Unesite naziv proizvoda" />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Količina *</label>
                <InputNumber value={itemDraft.quantity} onValueChange={(e) => setItemDraft((p) => ({ ...p, quantity: e.value || 0 }))} min={0} />
              </div>
            </div>

            <div className="col-12 md:col-3">
              <div className="field">
                <label>Jedinica *</label>
                <Dropdown value={itemDraft.unit} options={unitOptions} onChange={(e) => setItemDraft((p) => ({ ...p, unit: e.value }))} appendTo="self" />
              </div>
            </div>
            <div className="col-12 md:col-3">
              <div className="field">
                <label>Prioritet</label>
                <Dropdown value={itemDraft.priority} options={priorityOptions} onChange={(e) => setItemDraft((p) => ({ ...p, priority: e.value }))} appendTo="self" />
              </div>
            </div>
            <div className="col-12 md:col-3">
              <div className="field">
                <label>Rezervni deo</label>
                <div className="flex align-items-center gap-2 mt-2">
                  <Checkbox checked={!!itemDraft.isSparePart} onChange={(e) => setItemDraft((p) => ({ ...p, isSparePart: !!e.checked }))} />
                  <span>Da</span>
                </div>
              </div>
            </div>
            <div className="col-12 md:col-3 flex align-items-end">
              <Button label="Dodaj" icon="pi pi-plus" onClick={addItem} className="w-full" />
            </div>

            <div className="col-12">
              <div className="field">
                <label>Link (opciono)</label>
                <InputText value={itemDraft.link || ''} onChange={(e) => setItemDraft((p) => ({ ...p, link: e.target.value }))} placeholder="Unesite link" />
              </div>
            </div>
            <div className="col-12">
              <div className="field">
                <label>Opis (opciono)</label>
                <InputTextarea value={itemDraft.description || ''} onChange={(e) => setItemDraft((p) => ({ ...p, description: e.target.value }))} rows={3} placeholder="Unesite opis" />
              </div>
            </div>
          </div>

          <h3 className="m-0 mt-3 mb-2">Dodate stavke</h3>
          <DataTable value={items} emptyMessage="Nema dodatih stavki" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
            <Column
              header="R.br"
              body={(_: ProcurementItem, opts: any) => opts.rowIndex + 1}
              style={{ width: '80px' }}
            />
            <Column field="productName" header="Proizvod" />
            <Column header="Količina" body={(r: ProcurementItem) => r.quantity} style={{ width: '120px' }} />
            <Column field="unit" header="Jedinica" style={{ width: '120px' }} />
            <Column header="Prioritet" body={(r: ProcurementItem) => priorityLabel(r.priority)} style={{ width: '140px' }} />
            <Column header="Rezervni deo" body={(r: ProcurementItem) => (r.isSparePart ? 'Da' : 'Ne')} style={{ width: '130px' }} />
            <Column
              header="Opis"
              body={(r: ProcurementItem) =>
                r.description ? (
                  <Button
                    icon="pi pi-eye"
                    className="p-button-sm p-button-text"
                    onClick={() => openDescription(r.productName, r.description || '')}
                    tooltip="Prikaži opis"
                    tooltipOptions={{ position: 'top' }}
                  />
                ) : (
                  ''
                )
              }
              style={{ width: '90px' }}
            />
            <Column
              header="Akcije"
              body={(_: ProcurementItem, opts: any) => (
                <Button icon="pi pi-trash" severity="danger" className="p-button-sm" onClick={() => removeItem(opts.rowIndex)} />
              )}
              style={{ width: '120px' }}
            />
          </DataTable>

          <div className="field mt-3">
            <label>Napomene</label>
            <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Unesite napomene" />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={selectedRequest ? `Zahtev ${selectedRequest.requestId}` : 'Zahtev'}
        visible={viewVisible}
        onHide={() => setViewVisible(false)}
        // Full screen dialog (requested)
        maximized
        style={{ width: '100vw', height: '100vh' }}
        contentStyle={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}
        footer={viewFooter}
        draggable={false}
        modal
      >
        {!selectedRequest ? (
          <div>—</div>
        ) : (
          <div className="p-fluid">
            <div className="flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
              <div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{selectedRequest.requestId}</div>
                <div className="text-600">
                  Podnosilac: {selectedRequest.requestedBy?.firstName} {selectedRequest.requestedBy?.lastName}{' '}
                  ({selectedRequest.requestedBy?.username})
                </div>
                {selectedRequest.enteredBy?.username && (
                  <div className="text-600">
                    Uneo u ime podnosioca: {selectedRequest.enteredBy.firstName} {selectedRequest.enteredBy.lastName}
                  </div>
                )}
                <div className="text-600">Datum: {new Date(selectedRequest.createdAt).toLocaleString('sr-RS')}</div>
              </div>
              <div className="flex align-items-center gap-2" style={{ flexWrap: 'nowrap' }}>
                <Button
                  icon="pi pi-file-pdf"
                  severity="danger"
                  className="p-button-sm"
                  onClick={() => downloadRequestPdf(selectedRequest)}
                  tooltip="PDF"
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  icon="pi pi-file"
                  severity="success"
                  className="p-button-sm p-button-outlined"
                  onClick={() => downloadDeliveryPdf(selectedRequest)}
                  disabled={selectedRequest.status !== 'verified' || !selectedRequest.deliveryConfirmation}
                  tooltip="PDF isporuke"
                  tooltipOptions={{ position: 'top' }}
                />
                <Tag value={statusLabel(selectedRequest.status)} severity={statusSeverity(selectedRequest.status)} />
              </div>
            </div>

            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="text-600">Podneo</div>
                <div style={{ fontWeight: 700 }}>
                  {selectedRequest.requestedBy.firstName} {selectedRequest.requestedBy.lastName} ({selectedRequest.requestedBy.role})
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className="text-600">Kreiran</div>
                <div style={{ fontWeight: 700 }}>{formatDate(selectedRequest.createdAt)}</div>
              </div>
            </div>

            {(canProcess && selectedRequest.status === 'pending') || selectedRequest.status === 'rejected_with_feedback' || selectedRequest.status === 'rejected' ? (
              <div className="field mt-2">
                <label>Razlog / feedback</label>
                <InputTextarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={3} placeholder="Unesite razlog..." />
              </div>
            ) : null}

            {selectedRequest.status === 'delivered' && (
              <div className="field mt-2">
                <label>Napomena uz potvrdu isporuke (opciono)</label>
                <InputTextarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} rows={3} placeholder="Unesite napomenu..." />
              </div>
            )}

            <h3 className="m-0 mt-3 mb-2">Stavke</h3>
            <DataTable value={selectedRequest.items} className="p-datatable-sm" emptyMessage="Nema stavki" responsiveLayout="stack" breakpoint="768px">
              <Column field="productName" header="Proizvod" />
              <Column header="Količina" body={(r: ProcurementItem) => r.quantity} style={{ width: '120px' }} />
              <Column field="unit" header="Jedinica" style={{ width: '120px' }} />
              <Column header="Prioritet" body={(r: ProcurementItem) => priorityLabel(r.priority)} style={{ width: '140px' }} />
              <Column header="Rezervni deo" body={(r: ProcurementItem) => (r.isSparePart ? 'Da' : 'Ne')} style={{ width: '130px' }} />
              <Column
                header="Opis"
                body={(r: ProcurementItem) =>
                  r.description ? (
                    <Button
                      icon="pi pi-eye"
                      className="p-button-sm p-button-text"
                      onClick={() => openDescription(r.productName, r.description || '')}
                      tooltip="Prikaži opis"
                      tooltipOptions={{ position: 'top' }}
                    />
                  ) : (
                    ''
                  )
                }
                style={{ width: '90px' }}
              />
              <Column
                field="link"
                header="Link"
                body={(r: ProcurementItem) =>
                  r.link ? (
                    <a href={r.link} target="_blank" rel="noreferrer" title={r.link}>
                      Link
                    </a>
                  ) : (
                    ''
                  )
                }
              />
            </DataTable>

            <div className="field mt-3">
              <label>Napomene</label>
              <InputTextarea value={selectedRequest.notes || ''} readOnly rows={3} />
            </div>

            {selectedRequest.rejectionReason && (
              <div className="mt-2">
                <Message severity="warn" text={`Razlog: ${selectedRequest.rejectionReason}`} />
              </div>
            )}
            {selectedRequest.feedback && (
              <div className="mt-2">
                <Message severity="info" text={`Feedback: ${selectedRequest.feedback}`} />
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        header={confirmRequest ? `Potvrdi isporuku (${confirmRequest.requestId})` : 'Potvrdi isporuku'}
        visible={confirmVisible}
        onHide={() => setConfirmVisible(false)}
        // Full screen dialog (requested)
        maximized
        style={{ width: '100vw', height: '100vh' }}
        contentStyle={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}
        draggable={false}
        modal
      >
        {!confirmRequest ? (
          <div>—</div>
        ) : (
          <div className="p-fluid">
            <div className="mb-3 p-3" style={{ background: '#eff6ff', borderRadius: 6 }}>
              <div className="flex align-items-center gap-2">
                <i className="pi pi-info-circle" />
                <div>
                  Potvrdite da su sve stavke iz zahteva <strong>{confirmRequest.requestId}</strong> isporučene.
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Stavke zahteva ({confirmRequest.items?.length || 0})</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(confirmRequest.items || []).map((it, idx) => (
                  <li key={idx}>
                    {it.productName} - {it.quantity} {it.unit}
                    {it.itemStatus === 'otkazano' && <Tag value="Otkazano" severity="danger" className="ml-2" />}
                  </li>
                ))}
              </ul>
            </div>

            <div className="field">
              <label>Opciona napomena</label>
              <InputTextarea
                value={confirmNote}
                onChange={(e) => setConfirmNote(e.target.value)}
                rows={4}
                placeholder="Unesite napomenu za logistiku (opciono)"
              />
            </div>

            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                label="Nije sve isporučeno"
                className="p-button-danger p-button-outlined"
                onClick={() => confirmDeliveryFromModal('reject')}
                loading={loading}
              />
              <Button
                label="Sve je isporučeno"
                severity="success"
                onClick={() => confirmDeliveryFromModal('confirm')}
                loading={loading}
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header={descTitle ? `Opis - ${descTitle}` : 'Opis'}
        visible={descVisible}
        onHide={() => setDescVisible(false)}
        // Full screen (requested)
        maximized
        style={{ width: '100vw', height: '100vh' }}
        contentStyle={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}
        draggable={false}
        modal
      >
        <div className="p-fluid">
          <div className="text-600 mb-2">Opis stavke</div>
          <div
            style={{
              whiteSpace: 'pre-wrap',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: 12,
              minHeight: 120
            }}
          >
            {descText || '—'}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProcurementRequests;


